import type { Module } from 'vuex'
import { segmentActions } from './actions'
import type {
  SegmentState,
  ConditionSearchPickerChosenItemsMap,
} from './types'
import {
  generateSegmentConditionHashMap,
  generateSegmentConditionExpression,
  addSegmentConditionHash,
  removeSegmentConditionHash,
  getPrunedSegment,
  getPrunedSegmentFilter,
  generateScratchSegmentFromTag,
  generateScratchSegmentFromMessageSegment,
} from './utils'
import type { RootState } from '@/store/modules/types'
import { clone, mergeObjects } from '@/utils/helpers'
import { isSegmentConditionComplete } from '@/utils/segment'

export const initialSegmentState = (): SegmentState => ({
  segmentCriteriaGroups: [],
  segmentCriteriaMap: {},
  savedSegmentList: [],
  savedSegmentsPaginated: [],
  savedSegmentsCount: 0,
  isNoMoreSavedSegments: false,
  isFetchingSavedSegments: false,
  internalSegment: null,
  scratchSegment: null,
  scratchSegmentInfo: { source: null, sourceOid: null, changed: false },
  scratchSegmentConditionHashMap: {},
  segmentChosenItemsMap: {
    campaigns: {},
    events: {},
    pages: {},
    tags: {},
    segment: {},
  },
  isPatchingSegment: false,
  isCreatingSegment: false,
  isDeletingSegment: false,
})

const segmentModule: Module<SegmentState, RootState> = {
  namespaced: true,
  state: initialSegmentState,
  actions: segmentActions,
  getters: {
    isSegmentConditionComplete: () => (segmentCondition): boolean =>
      isSegmentConditionComplete(segmentCondition),

    getSegmentConditionExpression: state => (segmentCondition: { name: string }): string => {
      // console.trace( state );
      if (!segmentCondition) {
        console.log('segmentCondition is null')
        return ''
      }

      // console.log( segmentCondition )
      // console.log( state.segmentCriteriaMap[segmentCondition.name] )
      // console.log( state.segmentChosenItemsMap  )

      return generateSegmentConditionExpression(
        segmentCondition,
        state.segmentCriteriaMap[segmentCondition.name],
        state.segmentChosenItemsMap,
      )
    },

    activeSavedSegment: (state): Segment | null => {
      if (state.scratchSegmentInfo.source !== 'saved-audience-filter') return null
      return (
        state.savedSegmentList.find(
          filter => filter.oid === state.scratchSegmentInfo.sourceOid,
        ) || null
      )
    },

    bubbleUpFavoriteSegments: () => (filters: Segment[]) =>
      clone(filters).sort((a, b) =>
        a.favourite && !b.favourite ? -1 : b.favourite && !a.favourite ? 1 : 0,
      ),

    prunedScratchSegment: (state): Segment | null =>
      state.scratchSegment ? getPrunedSegment(state.scratchSegment) : null,

    scratchSegmentHasCompleteConditions: (state): boolean =>
      state.scratchSegment
        ? getPrunedSegment(state.scratchSegment).filter.conditions.length > 0
        : false,

    prunedScratchSegmentFilter: (state): any =>
      state.scratchSegment ? getPrunedSegmentFilter(state.scratchSegment) : null,

    audienceFilterCriteria: (state): any[] =>
      state.segmentCriteriaGroups.find(item => item.title === 'AUDIENCE')!.data,
  },
  mutations: {
    SET_SEGMENT_CRITERIA(state, segmentCriteriaGroups) {
      state.segmentCriteriaGroups = segmentCriteriaGroups
      const map = {}
      segmentCriteriaGroups.forEach(group =>
        group.data.forEach((criteria) => {
          map[criteria.resource] = criteria
        }),
      )
      state.segmentCriteriaMap = map
    },

    SET_SAVED_SEGMENT_LIST(state, filterList) {
      state.savedSegmentList = clone(filterList)
    },

    PATCH_SEGMENT_IN_SAVED_SEGMENT_LIST(state, { oid, editFilter }) {
      const patcher = filter => filter.oid === oid ? mergeObjects(filter, editFilter) : filter
      state.savedSegmentList = state.savedSegmentList.map(patcher)
      state.savedSegmentsPaginated = state.savedSegmentsPaginated.map(patcher)
    },

    SET_SAVED_SEGMENTS_PAGINATED(state, data) {
      state.savedSegmentsPaginated = clone(data?.rows || [])
    },
    CONCAT_SAVED_SEGMENTS_PAGINATED(state, data) {
      const newRows = data?.rows || []
      state.savedSegmentsPaginated = clone(state.savedSegmentsPaginated.concat(newRows))
    },
    SET_SAVED_SEGMENTS_COUNT(state, data) {
      state.savedSegmentsCount = data?.count || 0
    },
    SET_IS_NO_MORE_SAVED_SEGMENTS(state, data) {
      const rows = data?.rows || []
      state.isNoMoreSavedSegments = rows.length === 0 || state.savedSegmentsCount <= state.savedSegmentsPaginated.length
    },
    SET_IS_FETCHING_SAVED_SEGMENTS(state, isFetching: boolean) {
      state.isFetchingSavedSegments = isFetching
    },

    ADD_TO_SAVED_SEGMENT_LIST(state, filter) {
      state.savedSegmentList.unshift(clone(filter))
    },

    DELETE_FROM_SAVED_SEGMENT_LIST(state, deleteFilterOid) {
      state.savedSegmentList = state.savedSegmentList.filter(
        ({ oid }) => oid !== deleteFilterOid,
      )
    },

    SET_INTERNAL_SEGMENT(state, filter) {
      state.internalSegment = clone(filter)
    },

    SET_SCRATCH_SEGMENT(state, filter) {
      state.scratchSegment = clone(filter)
    },

    PATCH_SCRATCH_SEGMENT(state, editFilter) {
      if (!state.scratchSegment) return
      state.scratchSegment = mergeObjects(state.scratchSegment, editFilter)
    },

    SET_SCRATCH_SEGMENT_BY_SAVED_SEGMENT(state, filterOid) {
      const savedFilter = state.savedSegmentList.find(
        filter => filter.oid === filterOid,
      )
      if (!savedFilter || !state.scratchSegment) return

      state.scratchSegmentInfo = {
        source: 'saved-audience-filter',
        changed: false,
        sourceOid: filterOid,
      }
      state.scratchSegment = clone({ ...savedFilter, oid: state.scratchSegment.oid })
      state.scratchSegmentConditionHashMap = generateSegmentConditionHashMap(
        state.scratchSegment,
      )
    },

    SET_SCRATCH_SEGMENT_BY_AUDIENCE_TAG(state, tagOid: number) {
      if (!state.scratchSegment) return

      state.scratchSegmentInfo = {
        source: 'tag',
        changed: true,
        sourceOid: tagOid,
      }

      const newScratchSegment = generateScratchSegmentFromTag(tagOid, state.scratchSegment)
      state.scratchSegment = newScratchSegment
      state.scratchSegmentConditionHashMap = generateSegmentConditionHashMap(newScratchSegment)
    },
    SET_SCRATCH_SEGMENT_BY_MESSAGE_SEGMENT(state, { messageSegmentOid, filter }: { messageSegmentOid: number, filter: any }) {
      state.scratchSegmentInfo = {
        source: 'message-segment',
        changed: !!filter && filter.conditions && filter.conditions.length > 0,
        sourceOid: messageSegmentOid,
      }

      const newScratchSegment = generateScratchSegmentFromMessageSegment(messageSegmentOid, filter)
      state.scratchSegment = newScratchSegment

      state.scratchSegmentConditionHashMap = generateSegmentConditionHashMap(newScratchSegment)
    },
    SET_SCRATCH_SEGMENT_FROM_FILTER(state, { messageSegmentOid, filter }: { messageSegmentOid: number, filter: any }) {
      state.scratchSegmentInfo = {
        source: 'internal-audience-filter',
        changed: !!filter && filter.conditions && filter.conditions.length > 0,
        sourceOid: state.internalSegment?.oid,
      }

      const newScratchSegment = generateScratchSegmentFromMessageSegment(messageSegmentOid, filter)
      state.scratchSegment = newScratchSegment

      state.scratchSegmentConditionHashMap = generateSegmentConditionHashMap(newScratchSegment)
    },

    RESET_SCRATCH_SEGMENT(state) {
      if (!state.internalSegment || !state.scratchSegment) return

      state.scratchSegmentInfo = {
        source: 'internal-audience-filter',
        changed: false,
        sourceOid: state.internalSegment.oid,
      }
      state.scratchSegment = clone({
        ...state.internalSegment,
        name: '',
        oid: state.scratchSegment.oid,
      })
      state.scratchSegmentConditionHashMap = generateSegmentConditionHashMap(
        state.scratchSegment,
      )
    },

    RESET_SCRATCH_SEGMENT_FROM_MESSAGE_SEGMENT(state) {
      if (!state.internalSegment || !state.scratchSegment) return

      state.scratchSegmentInfo = {
        source: 'message-segment',
        changed: false,
        sourceOid: state.scratchSegmentInfo.sourceOid,
      }

      const newScratchSegment = clone({
        ...state.internalSegment,
        name: '',
        oid: state.scratchSegment.oid,
      })
      state.scratchSegment = newScratchSegment
      state.scratchSegmentConditionHashMap = generateSegmentConditionHashMap(newScratchSegment)
    },

    SET_SCRATCH_SEGMENT_CONDITION(state, { conditionIndex, filterCondition }) {
      if (!state.scratchSegment) return
      state.scratchSegment.filter.conditions[conditionIndex] = clone(filterCondition)
      state.scratchSegmentInfo = { ...state.scratchSegmentInfo, changed: true }
    },

    DELETE_SCRATCH_SEGMENT_CONDITION(state, conditionIndex) {
      if (!state.scratchSegment) return
      const scratchSegment = state.scratchSegment
      scratchSegment.filter.conditions.splice(conditionIndex, 1)
      scratchSegment.filter.logic.splice(conditionIndex, 1)
      state.scratchSegment = scratchSegment
      state.scratchSegmentConditionHashMap = removeSegmentConditionHash(
        state.scratchSegmentConditionHashMap,
        conditionIndex,
      )
    },

    ADD_SCRATCH_SEGMENT_CONDITION(state, { segmentCriteriaResource, logic }) {
      if (!state.scratchSegment) return
      if (!state.segmentCriteriaMap[segmentCriteriaResource]) return

      const segmentCriteria: SegmentCriteria = state.segmentCriteriaMap[segmentCriteriaResource]

      const newScratchSegment: Segment = clone(state.scratchSegment)

      const firstLogic = newScratchSegment.filter.logic[0]

      if (newScratchSegment.filter.conditions.length !== 0) {
        // Check if existing logic in scratch segment is unparenthesized and add parenthesis to it.
        if (logic && firstLogic && (String(firstLogic) != '(' || (String(firstLogic) === 'or' && logic != 'or'))) {
          newScratchSegment.filter.logic = ['(', ...newScratchSegment.filter.logic, ')']
        }

        if (logic && logic.length > 0) {
          newScratchSegment.filter.logic = newScratchSegment.filter.logic.concat(logic)
        }
        else {
          newScratchSegment.filter.logic[newScratchSegment.filter.logic.length] = firstLogic || 'and'
        }
      }

      const newCondition: SegmentCondition = window.useNuxtApp().$arUtils.segment.generateSegmentCondition(segmentCriteria, state.scratchSegmentInfo.source)

      newScratchSegment.filter.conditions[newScratchSegment.filter.conditions.length] = newCondition

      state.scratchSegment = newScratchSegment
      state.scratchSegmentConditionHashMap = addSegmentConditionHash(state.scratchSegmentConditionHashMap)

      state.scratchSegmentInfo = { ...state.scratchSegmentInfo, changed: true }
    },

    TOGGLE_SCRATCH_SEGMENT_LOGIC(state, logicIndex: number) {
      if (!state.scratchSegment) return

      const newScratchSegment: Segment = clone(state.scratchSegment)

      const newLogic = newScratchSegment.filter.logic[logicIndex] === 'and' ? 'or' : 'and'
      for (let i = 0; i < newScratchSegment.filter.logic.length; i += 1) {
        newScratchSegment.filter.logic[i] = newLogic
      }
      state.scratchSegment = newScratchSegment

      state.scratchSegmentInfo = { ...state.scratchSegmentInfo, changed: true }
    },
    ADD_TO_CONDITION_SEARCH_PICKER_CHOSEN_ITEM_MAP(
      state,
      payload: { resource: SegmentConditionSearchPickerCriteriaResource, items: any[] },
    ) {
      const newMap: ConditionSearchPickerChosenItemsMap = clone(state.segmentChosenItemsMap)
      if (payload.resource === 'campaigns') {
        payload.items.forEach((item) => { newMap.campaigns[item.oid] = item })
      }
      else if (payload.resource === 'events') {
        payload.items.forEach((item) => { newMap.events[item.oid] = item })
      }
      else if (payload.resource === 'tags') {
        payload.items.forEach((item) => { newMap.tags[item.oid] = item })
      }
      else if (payload.resource === 'pages') {
        payload.items.forEach((item) => { newMap.pages[item.puid] = item })
      }
      else if (payload.resource === 'segment') {
        payload.items.forEach((item) => { newMap.segment[item.oid] = item })
      }
      state.segmentChosenItemsMap = newMap
    },
    SET_IS_PATCHING_SEGMENT(state, isUpdating: boolean) {
      state.isPatchingSegment = isUpdating
    },
    SET_IS_CREATING_SEGMENT(state, isUpdating: boolean) {
      state.isCreatingSegment = isUpdating
    },
    SET_IS_DELETING_SEGMENT(state, isUpdating: boolean) {
      state.isDeletingSegment = isUpdating
    },
  },
}

export default segmentModule
